/**
 * As quatro seções do template que dependem só de conteúdo escrito:
 * patrocinadores, depoimentos, notícias e a galeria da marca.
 *
 * Ficam juntas porque compartilham a mesma regra, e ela é a parte importante
 * deste arquivo: **cada uma some da página enquanto não houver conteúdo real**.
 *
 * A alternativa seria preencher com exemplo — logos genéricos de patrocinador,
 * um capitão inventado elogiando a competição, três posts de blog que não
 * existem. Todas elas ficariam bonitas e todas seriam desmentidas pelo próprio
 * site: a lista de times está a um clique, e quem procurar o autor do elogio
 * não vai achar. Um site com seções a menos parece novo; um site com elogios
 * falsos parece outra coisa.
 *
 * Para ligar qualquer uma delas, basta preencher a lista correspondente em
 * `conteudo.ts`. O layout já está aqui, igual ao do template.
 */

import { useRef } from 'react';
import { Link } from 'react-router';
import { useEntradaDaSecao } from './animacoes';
import { PATROCINADORES, DEPOIMENTOS, NOTICIAS, MARCA } from './conteudo';

/* ------------------------------------------------------------------ *
 * Patrocinadores
 * ------------------------------------------------------------------ */

export function SecaoParceiros() {
  const raiz = useRef<HTMLElement>(null);
  useEntradaDaSecao(raiz, '[data-entra]');

  if (PATROCINADORES.length === 0) return null;

  return (
    <section className="sponsors-section" ref={raiz}>
      <div className="container">
        <div className="wrapper sponsors-wrapper">
          <h2 className="partners-title" data-entra>
            Patrocinadores e parceiros
          </h2>

          <div className="partner-logos">
            <div className="partner-logo-block" data-entra>
              {PATROCINADORES.map((p) => (
                <div key={p.nome} className="partner-logo-link">
                  <img src={p.logoUrl} alt={p.nome} loading="lazy" />
                </div>
              ))}
            </div>
          </div>

          <p className="partner-para" data-entra>
            Obrigado a quem faz a Hexa Cup acontecer.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Depoimentos
 * ------------------------------------------------------------------ */

export function SecaoDepoimentos() {
  const raiz = useRef<HTMLElement>(null);
  useEntradaDaSecao(raiz, '[data-entra]');

  if (DEPOIMENTOS.length === 0) return null;

  /*
   * O carrossel do template tem setas, bolinhas e um contador "1/4". Com um
   * depoimento só, tudo isso é ruído: a seta não leva a lugar nenhum e o
   * contador anuncia que existe mais do que existe. A navegação só aparece
   * quando há o que navegar.
   */
  const [primeiro] = DEPOIMENTOS;
  if (!primeiro) return null;

  return (
    <section className="testimonial-section" ref={raiz}>
      <div className="container">
        <div className="wrapper testimonial-wrapper">
          <div className="testimonial-slider" data-entra>
            <div className="testimonial-mask">
              <div className="testimonial-slide">
                <div className="testimonial-slider-content">
                  {primeiro.imagemUrl && (
                    <img
                      src={primeiro.imagemUrl}
                      alt=""
                      aria-hidden
                      loading="lazy"
                      className="testimonial-slider-img"
                    />
                  )}
                  <div className="testimonial-slider-details">
                    <p className="testimonial-slider-desp">{primeiro.texto}</p>
                    <div className="testimonial-author-details">
                      <div className="testimonial-author-name">— {primeiro.autor},</div>
                      <p className="testimonial-author-designation">{primeiro.papel}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Notícias
 * ------------------------------------------------------------------ */

export function SecaoNoticias() {
  const raiz = useRef<HTMLElement>(null);
  useEntradaDaSecao(raiz, '[data-entra]');

  if (NOTICIAS.length === 0) return null;

  return (
    <section className="blog-section" ref={raiz}>
      <div className="container">
        <div className="wrapper blog-wrapper">
          <div className="blog-top">
            <h2 className="partners-title" data-entra>
              Da beira do campo
            </h2>
            <p className="blog-para" data-entra>
              Notícias, bastidores e o que aconteceu nas últimas rodadas.
            </p>
          </div>

          <div className="collection-list-wrapper-blog">
            <div className="collection-list-blog">
              {NOTICIAS.map((noticia) => (
                <div key={noticia.destino} className="collection-item-blog" data-entra>
                  <Link to={noticia.destino} className="blog-item-link">
                    <img
                      src={noticia.imagemUrl}
                      alt=""
                      aria-hidden
                      loading="lazy"
                      className="blog-image"
                    />
                    <div className="blog-item-detail">
                      <h3 className="blog-item-name">{noticia.titulo}</h3>
                      <div className="blog-item-flex">
                        <div className="match-date">
                          <div className="match-date-text">{noticia.data}</div>
                        </div>
                        <div className="match-date">
                          <div className="match-date-text">{noticia.leitura}</div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Galeria da marca
 * ------------------------------------------------------------------ */

export function SecaoMarca() {
  const raiz = useRef<HTMLElement>(null);
  useEntradaDaSecao(raiz, '[data-entra]');

  return (
    <section className="brand-section" ref={raiz}>
      <div className="container">
        <div className="wrapper brand-wrapper">
          <div className="brand-top">
            <h2 className="fixtures-title" data-entra>
              {MARCA.titulo}
            </h2>
            <p className="large-paragraph brand-para" data-entra>
              {MARCA.texto}
            </p>
            <div className="fixtures-buttons" data-entra>
              <Link to={MARCA.botao.destino} className="transparent-button">
                <div className="transparent-button-text">{MARCA.botao.rotulo}</div>
                <div className="transparent-button-bg" aria-hidden />
              </Link>
            </div>
          </div>

          {/* A galeria só entra quando houver arte de divulgação de verdade. */}
          {MARCA.imagens.length > 0 && (
            <div className="brand-images">
              {MARCA.imagens.map((img, i) => (
                <div key={img.url} className={`brand-lightbox _0${i + 1}`} data-entra>
                  <img src={img.url} alt={img.alt} loading="lazy" className="brand-image" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
